<template>
  <div>
    <v-form @submit.prevent="onSubmit">
      <div v-if="showRouteMetadata" class="d-flex justify-space-between">
        <div class="d-flex align-center">
          <h4 class="d-flex align-center mb-0 mr-2">
            <router-link :to="'/route/' + route.id">
              <v-icon color="primary" class="mr-1">mdi-map-marker-path</v-icon>
              <span v-if="route.route_plan_id">{{ $tc('route') }} </span>{{ route.name }}
            </router-link>
            <router-link :to="`/vehicle/` + route.vehicle.id" class="ml-5 subheader">
              <v-icon color="primary">mdi-truck</v-icon>
              {{ route.vehicle.name }}
            </router-link>
          </h4>
          <v-chip small class="mx-2 align-self-center">
            <multi-time-display :time="route.start" />
          </v-chip>
          {{ $t('to') }}
          <v-chip small class="mx-2">
            <multi-time-display :time="route.end" />
          </v-chip>
          ~
          <v-chip small class="ml-2">
            <v-icon small class="ml-n1 mr-1">mdi-timer</v-icon>
            {{ route.duration | humanizeDuration(({ numberOfSignificantParts: 2, separator: ', ' })) }}
          </v-chip>
        </div>
      </div>
      <div v-if="showViolationSummary" class="mt-2 mb-3">
        <v-icon color="danger" large>mdi-alert</v-icon>
        <v-chip v-for="(violation, index) in violations" :key="index" class="ml-2" dark>
          {{ formatViolation(violation) }}
        </v-chip>
      </div>
      <v-row>
        <v-col lg="8" cols="12">
          <temperature-chart
            class="mb-2"
            v-if="showTemperatureGraph"
            ref="temperature-chart"
            :height="300"
            :data="temperatureData"
            :key="componentKey" />
          <div class="mb-2" v-if="shouldShowProbedTemperatures">
            <v-data-table
              hide-default-footer
              :headers="routeLocationsTableFields"
              :items="sortedRouteLocations">
              <template v-slot:item.location="row">
                <span v-if="row.value.type === 'dc'">
                  <v-icon>mdi-office-building</v-icon>
                </span>
                <span v-else>
                  <v-avatar size="25" color="blue-grey">
                    <span class="white--text">{{ row.index }}</span>
                  </v-avatar>
                </span>
                <span class="ml-3">{{ row.value.label }}</span>
              </template>
              <template v-for='(routeProductColumn, index) in routeProductColumns' v-slot:[`item.${routeProductColumn.value}`]="row">
                <if-set :value="getRouteLocationProductTemperatureValidator(row.item, routeProductColumn.route_product_id)" :key="index">
                  <template v-slot:default="scope">
                    <div v-if="!scope.value.$model.simulated_temperature_in_spec">
                      <v-text-field
                        outlined
                        hide-details
                        dense
                        :error="hasError(scope.value.probed_temperature)"
                        v-model="scope.value.$model.probed_temperature"
                        :placeholder="toString(scope.value.$model.simulated_temperature)"
                        :append-icon="customerContext.temperatureIcon()">
                      </v-text-field>
                    </div>
                    <div v-else>
                      <if-set :value="scope.value.$model.simulated_temperature">
                        {{ scope.value.$model.simulated_temperature }} &deg; {{ customerContext.tempFormat() }}
                      </if-set>
                    </div>
                  </template>
                </if-set>
              </template>
              <template v-slot:item.vehicle_arrival="row">
                <multi-time-display v-if="row.value" :time="row.value" />
                <span v-else class="text-danger text-nowrap">
                  <vf-tooltip :text="$t('eta')">
                    <v-icon>mdi-calendar-clock</v-icon>
                  </vf-tooltip>
                  <multi-time-display class="d-inline-block ml-1" :time="row.item.eta" />
                </span>
              </template>
              <template v-slot:item.vehicle_departure="row">
                <span class="text-nowrap">
                  <multi-time-display v-if="row.value" :time="row.value" />
                </span>
              </template>
              <template v-slot:item.duration="row">
                <span v-if="!row.item.unregistered_location" class="text-nowrap">
                  {{ row.item.getVehicleStopDuration() | humanizeDuration(({ numberOfSignificantParts: 2, separator: ', ' })) }}
                </span>
              </template>
            </v-data-table>
            <div v-if="probedTemperatureErrors" class="danger--text mt-2">
              <ul>
                <li v-for="(error, index) in probedTemperatureErrors" :key="index">
                  {{ error }}
                </li>
              </ul>
            </div>
          </div>
          <h6>{{ $t('notes_optional') }}:</h6>
          <resource-notes
            ref="rootCauseNotes"
            :showAddButton="false"
            :notes="routeAssessment.notes"
            :limitToContexts="['frontend/form/root-cause']" />
        </v-col>
        <v-col lg="4" cols="12">
          <v-radio-group
            dense
            v-model="routeAssessment.customer_cause"
            :label="$t('cause_of_violation')"
            :error-messages="customerCauseErrors">
            <v-radio
              v-for="(option, index) in customerCauseOptions" :key="index"
              :value="option.value"
              :label="option.text">
            </v-radio>
          </v-radio-group>
          <v-radio-group
            v-show="!requireProbedTemperatures"
            v-model="routeAssessment.product_ok"
            :label="$t('product_status')"
            :error-messages="productStatusErrors">
            <v-radio
              v-for="(option, index) in productStatusOptions" :key="index"
              :value="option.value"
              :label="option.text">
            </v-radio>
          </v-radio-group>
          <v-radio-group
            v-model="routeAssessment.customer_action"
            :label="$tc('action')"
            :error-messages="customerActionErrors">
            <v-radio
              v-for="(option, index) in customerActionOptions" :key="index"
              :value="option.value"
              :label="option.text">
            </v-radio>
          </v-radio-group>
          <div class="mt-3">
            <!-- do the gtag in the submit instead of here -->
            <v-btn :loading="isSaving" type="submit" color="primary">
              <v-icon class="mr-2">mdi-clipboard-check</v-icon>
              {{ $t('submit_assessment') }}
            </v-btn>
          </div>
        </v-col>
      </v-row>
    </v-form>
  </div>
</template>

<script>
import RouteViolationRecord from '@/orm/models/RouteViolationRecord'
import RouteLocationRecord from '@/orm/models/RouteLocationRecord'
import ApiUtilitiesMixin from '@/components/mixins/ApiUtilitiesMixin'
import CurrentUserMixin from '@/components/mixins/CurrentUserMixin'
import DateTimeUtilsMixin from '@/components/mixins/DateTimeUtilsMixin'
import ChartMixin from '@/components/mixins/ChartMixin'
import TemperatureMixin from '@/components/mixins/TemperatureMixin'
import ValidationMixin from '@/components/mixins/ValidationMixin'
import MultiTimeDisplay from '@/components/shared/MultiTimeDisplay'
import TemperatureChart from '@/components/shared/chart/TemperatureChart'
import ResourceNotes from '@/components/shared/ResourceNotes.vue'
import VfTooltip from '@/components/shared/VfTooltip'
import { required, requiredIf, decimal, between } from 'vuelidate/lib/validators'
import { cloneDeep, filter, map, uniqBy, sortBy, get, toString, round, each } from 'lodash'

export default {
  mixins: [
    ApiUtilitiesMixin,
    CurrentUserMixin,
    DateTimeUtilsMixin,
    ChartMixin,
    TemperatureMixin,
    ValidationMixin
  ],
  components: {
    MultiTimeDisplay,
    TemperatureChart,
    ResourceNotes,
    VfTooltip
  },
  validations: {
    routeAssessment: {
      customer_cause: {
        required
      },
      customer_action: {
        required
      },
      product_ok: {
        required
      }
    },
    outOfSpecProductTemperatures: {
      $each: {
        probed_temperature: {
          requiredIf: requiredIf(function (value) {
            return this.requireProbedTemperatures === true
          }),
          between (value) {
            return between(this.minimumProbeTemperature, this.maximumProbeTemperature)(value)
          },
          decimal
        }
      }
    }
  },
  props: {
    model: {
      type: Object
    },
    route: {
      type: Object
    },
    customerAssessmentTerms: {
      type: Array
    },
    showRouteMetadata: {
      type: Boolean,
      default: false
    },
    showViolationSummary: {
      type: Boolean,
      default: false
    },
    showTemperatureGraph: {
      type: Boolean,
      default: false
    },
    showProbedTemperatures: {
      type: Boolean,
      default: false
    },
    requireProbedTemperatures: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      routeAssessment: this.initializeForm(),
      isSaving: false,
      componentKey: 0,
      outOfSpecProductTemperatures: this.generateOutOfSpecTemperatureArray(),
      maximumProbeTemperature: 100,
      minimumProbeTemperature: -100
    }
  },
  methods: {
    toString: toString,

    /**
     * Constructs an array objects containing temperature details (recorded and
     * simulated) for stops where the temperature was out of spec. The object
     * contains the product's simulated and probed temperature as well as
     * some helper data/functions to determine if those values are in spec.
     *
     * @return Array<Object>
     */
    generateOutOfSpecTemperatureArray () {
      const outOfSpecTemperatures = []
      this.route.route_locations.forEach(routeLocation => {
        // Skip anything that isn't a delivery location and missed stops as we don't need to collect probed
        // temperatures for places we never actually visited. This matches the logic in this.sortedRouteLocations()
        // which I tried to use here but the property wasn't computed by the time this function needed to run.
        if (routeLocation.vehicle_arrival == null || !routeLocation.location.isDeliveryLocation()) {
          return
        }

        this.route.route_products.forEach(routeProduct => {
          // Simulated temperatures from a route location are always provided in celsius so convert them to the
          // customer's scale.
          const simulatedTemperature = this.temperatureToCustomerScale(get(routeLocation, `simulated_temperatures_at_arrival.${routeProduct.id}`), 'c', routeProduct.temperature_scale, true) || this.$t('not_set')
          const filteredRouteProductRecordedTemperatures = filter(routeLocation.route_product_recorded_temperatures, ['route_product_id', routeProduct.id])
          const routeProductRecordedTemperature = get(filteredRouteProductRecordedTemperatures, '0')
          const simulatedTemperatureInSpec = simulatedTemperature != null && routeProduct.product.min_temp <= simulatedTemperature && simulatedTemperature <= routeProduct.product.max_temp

          if (!simulatedTemperatureInSpec) {
            const recordedTemperature = {
              route_location: routeLocation,
              route_product: routeProduct,
              product_id: routeProduct.product_id,
              simulated_temperature: simulatedTemperature,
              // Round to address fractional changed when converting from Celsius to Fahrenheit.
              // If routeProductRecordedTemperature.temperature doesn't exist round() returns NaN (which fouls the
              // decimal and between validation) so fall back to null if that's the case.
              probed_temperature: round(get(routeProductRecordedTemperature, 'temperature'), 1) || null,
              recorded_temperature: routeProductRecordedTemperature,
              simulated_temperature_in_spec: simulatedTemperatureInSpec,
              probed_temperature_in_spec: function () {
                return this.probed_temperature != null &&
                  routeProduct.product.min_temp <= this.probed_temperature &&
                  this.probed_temperature <= routeProduct.product.max_temp
              }
            }
            outOfSpecTemperatures.push(recordedTemperature)
          }
        })
      })
      return outOfSpecTemperatures
    },
    initializeForm () {
      return cloneDeep(this.model) || this.$orm('RouteAssessment').createRecord({
        id: undefined,
        route_id: this.route.id,
        customer_cause: null,
        customer_action: null,
        product_ok: null,
        // Declared up front so it is reactive and so appending a new note below cannot dereference undefined
        notes: []
      })
    },

    /**
     * Validates the form and fires off several API requests to create the route assessment, notes, and any route
     * product recorded temperatures.
     *
     * @return undefined
     */
    async onSubmit () {
      // When we required probe temperatures we assume the product status based on whether the probed
      // temperatures were all in-spec or not, and we do not show the product status radio button. Therefore, if probe
      // temperatures are required we need to inspect the probe temperatures and set the product status automatically.
      if (this.requireProbedTemperatures) {
        this.routeAssessment.product_ok = true
        this.outOfSpecProductTemperatures.forEach((outOfSpecProductTemperature) => {
          if (!outOfSpecProductTemperature.simulated_temperature_in_spec &&
            !outOfSpecProductTemperature.probed_temperature_in_spec()) {
            this.routeAssessment.product_ok = false
          }
        })
      }

      // Validate the form and halt processing if there is an error
      this.$v.$touch()
      if (this.$v.$invalid) return false

      this.isSaving = true

      // Set when a write that follows the assessment itself fails; see the comment at the note and temperature
      // writes below.
      let secondarySaveError = null
      let secondarySaveMessage = null

      try {
        // Create the assesment...
        const savedAssessment = await this.$orm('RouteAssessment')
          .createOrUpdate(this.routeAssessment, {
            params: { expand: 'notes' }
          })

        // The assessment is persisted from here on, so the writes below are secondary: if one of them fails we
        // report it and carry on rather than letting it abort the flow. Without the 'saved' event this page, the
        // side nav badge and the pending assessment list all keep showing the route as unassessed when it is not,
        // and the user is left thinking nothing saved at all (VF-4395).

        // ...and associated note (if one was supplied)
        const newNoteBody = this.$refs.rootCauseNotes.getNewNoteBody()
        if (newNoteBody !== undefined && newNoteBody.trim().length !== 0) {
          const newNote = {
            resource: 'RouteAssessment',
            entity_id: savedAssessment.id,
            user_id: this.user.id,
            note: newNoteBody,
            context: 'frontend/form/root-cause'
          }

          try {
            const note = await this.$orm('Note').createOrUpdate(newNote, {
              params: { expand: 'user_email', fields: 'id,note,created,user_email,context' }
            })

            this.$refs.rootCauseNotes.resetNoteBody()
            this.routeAssessment.notes.push(note)
          } catch (error) {
            secondarySaveError = error
            secondarySaveMessage = this.$t('note_not_saved')
            await this.$store.dispatch('globalMessagingModule/showErrorFlashBanner', secondarySaveMessage)
          }
        }

        // For each out of spec temperature for which the user supplied a manually observed temperature, create a
        // promise (that calls the API to create a RouteProductRecordedTemperature)
        const routeProductRecordedTemperaturePromises = []
        this.outOfSpecProductTemperatures.forEach(outOfSpecProductTemperature => {
          // If no temperature was provided or the temperature provided already matches the temperature stored then we
          // don't need to queue an API request to create/update the value
          if (outOfSpecProductTemperature.probed_temperature == null ||
            outOfSpecProductTemperature.probed_temperature === get(outOfSpecProductTemperature, 'recorded_temperature.temperature')) {
            return
          }

          const recordedTemperature = outOfSpecProductTemperature.recorded_temperature || {
            timestamp: outOfSpecProductTemperature.route_location.vehicle_arrival,
            route_product_id: outOfSpecProductTemperature.route_product.id,
            route_location_id: outOfSpecProductTemperature.route_location.id,
            source: 'manual_probe',
            temperature_scale: this.customerContext.tempFormat()
          }
          recordedTemperature.temperature = outOfSpecProductTemperature.probed_temperature

          routeProductRecordedTemperaturePromises.push(this.$orm('RouteProductRecordedTemperature').createOrUpdate(recordedTemperature))
        })

        try {
          await Promise.all(routeProductRecordedTemperaturePromises)
        } catch (error) {
          secondarySaveError = error
          secondarySaveMessage = this.$t('assessment_saved_with_errors')
          this.renderRecordSaveErrorFlashBanner(error)
        }

        // The assessment itself saved, so let the caller refresh regardless of any secondary failure above. The
        // message goes with it because the banners raised above are flash banners: a caller that navigates on save
        // (RootCauseView) has them cleared by the router's beforeEach and would otherwise report a clean save.
        this.$emit('saved', this.routeAssessment, secondarySaveMessage)
      } catch (error) {
        if (error) {
          this.renderRecordSaveErrorFlashBanner(error)
          throw (error)
        }
      } finally {
        this.isSaving = false
      }

      // The user has been told and the caller has refreshed, so rethrow any secondary failure to get it logged in
      // Kibana - the convention ErrorMixin describes as "rethrow the error so we log it". Vue routes the rejection
      // of this handler to Vue.config.errorHandler, which reports it without showing anything further.
      if (secondarySaveError !== null) {
        throw secondarySaveError
      }
    },

    /**
     * Translates the violation type from a sting code to a human-readable (language specific string)
     * @param {RouteViolationRecord} violation
     * @return String
     */
    formatViolation (violation) {
      let result = ''
      if (violation.route_product != null && violation.route_product.product != null) {
        result = `${violation.route_product.product.name} - `
      }
      result += this.$tc('fatal_violation:' + violation.type)
      return result
    },

    /**
     * Returns either the validator (which contains both the model and the validation state of the model) of a required
     * probe temperature (when the simulated temperature is out of spec) or the simulated temperature.
     * @param routeLocation
     * @param routeProductId
     * @returns {*}
     */
    getRouteLocationProductTemperatureValidator (routeLocation, routeProductId) {
      const filteredProductLocationTemperatureValidator = filter(this.$v.outOfSpecProductTemperatures.$each.$iter, productLocationTemperatureValidator => {
        if (productLocationTemperatureValidator.$model === undefined) {
          return false
        }
        return productLocationTemperatureValidator.$model.route_product.id === routeProductId && productLocationTemperatureValidator.$model.route_location.id === routeLocation.id
      })

      // returns either the validator (which will be at position 0 after the filter above) or if there are no results
      // from the filter then returns an object with the simulated temperature.
      return get(filteredProductLocationTemperatureValidator, '0', {
        $model: {
          simulated_temperature_in_spec: true,
          simulated_temperature: this.temperatureToCustomerScale(get(routeLocation, `simulated_temperatures_at_arrival.${routeProductId}`), 'c', this.customerContext.tempFormat(), true)
        }
      })
    }
  },
  computed: {
    // Convenience error message properties for Vuetify
    customerCauseErrors () {
      return this.validationError({
        prop: this.$v.routeAssessment.customer_cause,
        message: this.$t('required')
      })
    },
    productStatusErrors () {
      return this.validationError({
        prop: this.$v.routeAssessment.product_ok,
        message: this.$t('required')
      })
    },
    customerActionErrors () {
      return this.validationError({
        prop: this.$v.routeAssessment.customer_action,
        message: this.$t('required')
      })
    },
    /**
     * Kind of a hacky computed property that gets all the errors in the probed temperature form fields. This will
     * list every single issue in the form, which is kinda overkill, but it feels like it could be confusing if we're
     * only showing one error. Another option would be showing each error under the constituent v-text-field, but this
     * requires disabling 'hide-details' and it makes the table look weird.
     *
     * Might just need to rethink the presentation on this page, as it's already kind of duplicating the redesigned
     * location list. Also, this feature has been in the codebase for about a year as of this comment (october 20220)
     * and nobody has it enabled yet, so it may yet be changed before it's released to any customer. For now though,
     * this should work okay.
     *
     * @returns {*[]}
     */
    probedTemperatureErrors () {
      const errors = []
      each(this.$v.outOfSpecProductTemperatures.$each.$iter, item => {
        if (this.hasError(item)) {
          if (item.probed_temperature.requiredIf === false) {
            errors.push(this.$t('probe_temperature_required'))
          }
          if (item.probed_temperature.decimal === false) {
            errors.push(this.$t('must_be_decimal'))
          }
          if (item.probed_temperature.between === false) {
            const message = this.$t('temp_value_error', {
              min_temp: this.temperatureToCustomerScale(-100),
              max_temp: this.temperatureToCustomerScale(100),
              temperatureFormat: this.customerContext.tempFormat()
            })
            errors.push(message)
          }
        }
      })
      return errors
    },
    /**
     * @return Boolean
     */
    shouldShowProbedTemperatures () {
      return this.showProbedTemperatures || this.requireProbedTemperatures
    },

    /**
     * Returns an array of field keys and names (translated) that should be
     * rendered in the locations table. The number of fields is variable based
     * on the number of products in the route.
     *
     * @return Array
     */
    routeLocationsTableFields () {
      const fields = []
      fields.push({ value: 'location', text: this.$tc('location') })
      each(this.routeProductColumns, x => {
        fields.push(x)
      })
      fields.push({ value: 'vehicle_arrival', text: this.$t('arrival') })
      fields.push({ value: 'vehicle_departure', text: this.$t('departure') })
      fields.push({ value: 'duration', text: this.$t('duration') })

      return fields
    },

    /**
     * Sort by the route locations by vehicle arrival time, removing anything
     * that isn't a delivery location as well as unregistered and missed stops
     * (as we'll never have temperatures for those).
     *
     * @return Array<RouteLocationRecord>
     */
    sortedRouteLocations () {
      let routeLocations = filter(this.route.route_locations, (routeLocation) => routeLocation.vehicle_arrival != null)
      routeLocations = filter(routeLocations, (routeLocation) => routeLocation.location.isDeliveryLocation())
      routeLocations.sort(RouteLocationRecord.sortByTypeAndArrivalTime)
      return routeLocations
    },

    routeProductColumns () {
      return map(this.route.route_products, rp => {
        return {
          value: `product_${rp.product_id}`,
          text: rp.product.name,
          route_product_id: rp.id
        }
      })
    },

    /**
     * Returns an object with all the data required to render a temperature
     * chart
     *
     * @return Object see ChartMixin.formatRouteChartData for details
     */
    temperatureData () {
      if (!this.showTemperatureGraph) {
        return {}
      }
      return this.formatRouteChartData(this.route, this.route.interpolated_sensor_data)
    },

    /**
     * @return Array<RouteViolationRecord>
     */
    violations () {
      // Show only fatal violations
      let violations = filter(this.route.route_violations, x => x.level === RouteViolationRecord.LEVEL_FATAL)
      // Combine violations of the same type but differentiate between route products.
      violations = uniqBy(violations, violation => {
        let result = ''
        if (violation.route_product) {
          result += violation.route_product.id + '|'
        }
        result += violation.type
        return result
      })
      return sortBy(violations, violation => violation.type)
    },

    /**
     * Returns an array of text / value options to show in the root cause
     * section
     *
     * @return Array<{text,value}>
     */
    customerCauseOptions () {
      const result = filter(this.customerAssessmentTerms, term => term.type === 'cause' && (!term.isArchived || term.id === get(this, 'model.customer_cause')))
      return map(result, (term) => {
        return {
          text: term.value,
          value: term.id
        }
      })
    },

    /**
     * Returns an array of text / value options to show in the action section
     *
     * @return Array<{text,value}>
     */
    customerActionOptions () {
      const result = filter(this.customerAssessmentTerms, term => term.type === 'action' && (!term.isArchived || term.id === get(this, 'model.customer_action')))
      return map(result, (term) => {
        return { text: term.value, value: term.id }
      })
    },

    /**
     * Options property for product status picker
     * @returns Object
     */
    productStatusOptions () {
      return [
        {
          text: this.$t('OK'),
          value: 1
        },
        {
          text: this.$t('Not OK'),
          value: 0
        }
      ]
    }
  },
  mounted () {
    this.maximumProbeTemperature = this.temperatureToCustomerScale(100)
    this.minimumProbeTemperature = this.temperatureToCustomerScale(-100)
  },
  watch: {
    /**
     * Watch route property. If the route id changes, then reset the form.
     * This is mostly useful to deal with table refreshes in RootCauseAnalysis from pulling the rug out from under us.
     *
     * @param newValue
     * @param oldValue
     */
    route (newValue, oldValue) {
      if (newValue.id !== oldValue.id) {
        this.routeAssessment = this.initializeForm()
        this.outOfSpecProductTemperatures = this.generateOutOfSpecTemperatureArray()
        this.$v.$reset()
      }
    },
    temperatureData (newValue, oldValue) {
      this.componentKey += 1
    }
  }
}
</script>

<style>
